<template>
  <div class="isolation-aging-parameters">
    <div v-if="!isLoading && parameters">
      <div class="isolation-aging-parameters-general mb-3">
        <div class="isolation-aging-parameters-general__title">
          {{ $t('engineering.isolation-aging.parameters-period-section') }}
          <info-badge :string-key="titleTooltip" />
        </div>
        <div class="p-3">
          <b-row>
            <b-col
              sm="6"
            >
              <div
                v-if="isVisible('days')"
                class="isolation-aging-parameters-general__input form-group"
              >
                <div class="mb-3">
                  <strong>{{ $t('engineering.isolation-aging.parameters.moving-period') }}</strong>
                  <info-badge :translation-key="'messages.MI038.message'" />
                </div>
                <label>
                  {{ getLabel('days') }}<span v-if="isRequired('days')">*</span>
                </label>
                <s-input-label-edit
                  v-model="$v.parameters.days.$model"
                  :formatted-value="getFormattedNumber(
                      $v.parameters.days.$model,
                      'days'
                    )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="$v.parameters.days.$model"
                    :numeric-options="getNumericOptions('days')"
                    :validation="$v.parameters['days']"
                    :invalid="getErrorState('days')"
                    :disabled="isDisabled('days')"
                  />
                  <b-form-invalid-feedback
                    v-if="getErrorState('days')"
                    id="input-live-feedback-initial-date"
                  >
                    <span v-html="getValidationFeedback('days')"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>

            <b-col
              sm="12"
              md="6"
            >
              <b-row class="mb-3">
                <b-col>

                  <b-checkbox
                    v-model="$v.parameters.fixedPeriod.$model"
                    :disabled="isDisabled('fixedPeriod')"
                    @change="onChangeFixedPeriodHandler"
                  >
                    {{ getLabel('fixedPeriod') }}
                  </b-checkbox>

                </b-col>
              </b-row>
              <div class="px-4">
                <b-row>
                  <b-col sm="6">

                    <div
                      v-if="isVisible('initialDate')"
                      class="isolation-aging-parameters-general__input form-group"
                    >
                      <label>
                        {{ getLabel('initialDate') }}<span v-if="isRequired('initialDate')">*</span>
                      </label>
                      <s-input-label-edit
                        v-model="$v.parameters.initialDate.$model"
                        :formatted-value="getFormattedDate($v.parameters.initialDate.$model)"
                        :editable="editMode"
                      >
                        <s-input-date-picker
                          v-model="$v.parameters.initialDate.$model"
                          :min-date="minDate"
                          :boundary="'window'"
                          :disabled="isDisabled('initialDate')"
                          :state="getErrorBsState('initialDate')"
                          @lazy-blur-native-proxy="onChangeDateTime('initial-date')"
                          @input-calendar-click="onChangeDateTime('initial-time')"
                        />
                        <b-form-invalid-feedback
                          v-if="getErrorState('initialDate')"
                          class="d-block"
                          id="input-live-feedback-initial-date"
                        >
                          <div
                            v-if="!$v.parameters.initialDate.isMinDateValid"
                            v-html="getMinDateValidationFeedback('initialDate')"
                          ></div>

                        </b-form-invalid-feedback>
                      </s-input-label-edit>
                    </div>
                  </b-col>
                  <b-col sm="6">
                    <div
                      v-if="isVisible('initialTime')"
                      class="isolation-aging-parameters-general__input form-group"
                    >
                      <label>
                        {{ getLabel('initialTime') }}<span v-if="isRequired('initialTime')">*</span>
                      </label>
                      <s-input-label-edit
                        v-model="$v.parameters.initialTime.$model"
                        :formatted-value="$v.parameters.initialTime.$model"
                        :editable="editMode"
                      >
                        <s-input-time
                          v-model="$v.parameters.initialTime.$model"
                          step="1"
                          :disabled="isDisabled('initialTime')"
                          :required="parametersFields.initialTime.required"
                          :state="getErrorBsState('initialTime')"
                          @blur-native-proxy="onChangeDateTime('initial-time')"
                          @input-time-now-click="onChangeDateTime('initial-time')"
                        />
                      </s-input-label-edit>
                    </div>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="6">

                    <div
                      v-if="isVisible('finalDate')"
                      class="isolation-aging-parameters-general__input form-group"
                    >
                      <label>
                        {{ getLabel('finalDate') }}<span v-if="isRequired('finalDate')">*</span>
                      </label>
                      <s-input-label-edit
                        v-model="$v.parameters.finalDate.$model"
                        :formatted-value="getFormattedDate($v.parameters.finalDate.$model)"
                        :editable="editMode"
                      >
                        <s-input-date-picker
                          v-model="$v.parameters.finalDate.$model"
                          :min-date="minDate"
                          :boundary="'window'"
                          :disabled="isDisabled('finalDate')"
                          :state="getErrorBsState('finalDate')"
                          @lazy-blur-native-proxy="onChangeDateTime('final-date')"
                          @input-calendar-click="onChangeDateTime('final-time')"
                        />
                        <b-form-invalid-feedback
                          v-if="getErrorState('finalDate')"
                          class="d-block"
                          id="input-live-feedback-final-date"
                        >
                          <div
                            v-if="!$v.parameters.finalDate.isMinDateValid"
                            v-html="getMinDateValidationFeedback('finalDate')"
                          ></div>
                        </b-form-invalid-feedback>
                      </s-input-label-edit>
                    </div>

                  </b-col>
                  <b-col sm="6">
                    <div
                      v-if="isVisible('finalTime')"
                      class="isolation-aging-parameters-general__input form-group"
                    >
                      <label>
                        {{ getLabel('finalTime') }}<span v-if="isRequired('finalTime')">*</span>
                      </label>
                      <s-input-label-edit
                        v-model="$v.parameters.finalTime.$model"
                        :formatted-value="$v.parameters.finalTime.$model"
                        :editable="editMode"
                      >
                        <s-input-time
                          v-model="$v.parameters.finalTime.$model"
                          step="1"
                          :disabled="isDisabled('finalTime')"
                          :required="parametersFields.finalTime.required"
                          :state="getErrorBsState('finalTime')"
                          @blur-native-proxy="onChangeDateTime('final-time')"
                          @input-time-now-click="onChangeDateTime('final-time')"
                        />
                      </s-input-label-edit>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-col>

          </b-row>
        </div>
      </div>

      <div class="isolation-aging-parameters-general avoid-break mb-3">
        <div class="isolation-aging-parameters-general__title">
          {{ $t('engineering.isolation-aging.parameters-humidity-section') }}
        </div>
        <div class="p-3">
          <b-row class="mb-3">
            <b-col>
              <b-checkbox
                v-model="$v.parameters.considerHumidity.$model"
                :disabled="isDisabled('considerHumidity')"
                @change="onChangeConsiderHumidityHandler"
              >
                {{ getLabel('considerHumidity') }}
              </b-checkbox>
            </b-col>
          </b-row>
          <div class="px-4">
            <b-row>
              <b-col
                sm="7"
                md="8"
              >
                <div
                  v-if="isVisible('paperHumiditySource')"
                  class="
                    isolation-aging-parameters-general__input
                    form-group position-relative
                  "
                >
                  <label>
                    {{ getLabel('paperHumiditySource') }}<span v-if="isRequired(
                        'paperHumiditySource'
                    )">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.paperHumiditySource.$model"
                    :formatted-value="$v.parameters.paperHumiditySource.$model
                      | enumFilter('engineering.isolation-aging')"
                    :editable="editMode"
                  >
                    <s-select
                      v-model="$v.parameters.paperHumiditySource.$model"
                      skip-initialize-value
                      :options="humiditySourceOptions"
                      :disabled="isDisabled('paperHumiditySource')"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('paperHumiditySource')"
                      id="input-live-feedback-paper-humidity-source"
                    >
                      <span v-html="getValidationFeedback('paperHumiditySource')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
              <b-col
                sm="5"
                md="4"
              >
                <div
                  v-if="isVisible('paperHumidity')"
                  class="isolation-aging-parameters-general__input form-group"
                >
                  <label>
                    {{ getLabel('paperHumidity') }}<span v-if="isRequired('paperHumidity')">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.paperHumidity.$model"
                    :formatted-value="getFormattedNumber(
                          $v.parameters.paperHumidity.$model,
                          'paperHumidity'
                        )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.paperHumidity.$model"
                      :numeric-options="getNumericOptions('paperHumidity')"
                      :validation="$v.parameters['paperHumidity']"
                      :invalid="getErrorState('paperHumidity')"
                      :disabled="isDisabled('paperHumidity')"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('paperHumidity')"
                      id="input-live-feedback-paper-humidity"
                    >
                      <span v-html="getValidationFeedback('paperHumidity')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col
                sm="7"
                md="8"
              >
                <div
                  v-if="isVisible('referenceHumidity')"
                  class="
                    isolation-aging-parameters-general__input
                    form-group position-relative
                  "
                >
                  <label>
                    {{ getLabel('referenceHumidity') }}<span v-if="isRequired(
                      'referenceHumidity'
                    )">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.referenceHumidity.$model"
                    :formatted-value="$v.parameters.referenceHumidity.$model
                   | enumFilter('engineering.isolation-aging')"
                    :editable="editMode"
                  >
                    <s-enum-select
                      v-model="$v.parameters.referenceHumidity.$model"
                      no-select-option
                      skip-initialize-value
                      translation-path="enums.engineering.isolation-aging"
                      :enum-definition="referenceHumidity"
                      :disabled="isDisabled('referenceHumidity')"
                      @input="onChangeReferenceHumidityHandler"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('referenceHumidity')"
                      id="input-live-feedback-reference-humidity"
                    >
                      <span v-html="getValidationFeedback('referenceHumidity')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
              <b-col
                sm="5"
                md="4"
              >
                <div
                  v-if="isVisible('referenceHumidityPercentage')"
                  class="isolation-aging-parameters-general__input form-group"
                >
                  <label>
                    {{ getLabel('referenceHumidityPercentage') }}<span v-if="isRequired(
                      'referenceHumidityPercentage'
                    )">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.referenceHumidityPercentage.$model"
                    :formatted-value="getFormattedNumber(
                          $v.parameters.referenceHumidityPercentage.$model,
                          'referenceHumidityPercentage'
                        )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.referenceHumidityPercentage.$model"
                      :numeric-options="getNumericOptions('referenceHumidityPercentage')"
                      :validation="$v.parameters['referenceHumidityPercentage']"
                      :invalid="getErrorState('referenceHumidityPercentage')"
                      :disabled="isDisabled('referenceHumidityPercentage')"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('referenceHumidityPercentage')"
                      id="input-live-feedback-reference-humidity-percentage"
                    >
                      <span v-html="getValidationFeedback('referenceHumidityPercentage')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col
                sm="7"
                md="8"
              >
                <div
                  v-if="isVisible('humidityCurveMethod')"
                  class="
                    isolation-aging-parameters-general__input
                    form-group position-relative
                  "
                >
                  <label>
                    {{ getLabel('humidityCurveMethod') }}<span v-if="isRequired(
                        'humidityCurveMethod'
                    )">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.humidityCurveMethod.$model"
                    :formatted-value="$v.parameters.humidityCurveMethod.$model
                   | enumFilter('engineering.isolation-aging')"
                    :editable="editMode"
                  >
                    <s-enum-select
                      v-model="$v.parameters.humidityCurveMethod.$model"
                      no-select-option
                      skip-initialize-value
                      translation-path="enums.engineering.isolation-aging"
                      :enum-definition="humidityCurve"
                      :disabled="isDisabled('humidityCurveMethod')"
                      @input="onChangeHumidityCurveMethodHandler"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('humidityCurveMethod')"
                      id="input-live-feedback-humidity-curve-method"
                    >
                      <span v-html="getValidationFeedback('humidityCurveMethod')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
              <b-col
                sm="5"
                md="4"
              >
                <div
                  v-if="isVisible('oxygenOil')"
                  class="
                  isolation-aging-parameters-general__input
                  form-group position-relative
                "
                >
                  <label>
                    {{ getLabel('oxygenOil') }}<span v-if="isRequired('oxygenOil')">*</span>
                  </label>
                  <s-input-label-edit
                    v-model="$v.parameters.oxygenOil.$model"
                    :formatted-value="getFormattedNumber(
                          $v.parameters.oxygenOil.$model,
                          'oxygenOil'
                        )"
                    :editable="editMode"
                  >
                    <s-number-input
                      class="text-right"
                      v-model="$v.parameters.oxygenOil.$model"
                      :numeric-options="getNumericOptions('oxygenOil')"
                      :validation="$v.parameters['oxygenOil']"
                      :invalid="getErrorState('oxygenOil')"
                      :disabled="isDisabled('oxygenOil')"
                    />
                    <b-form-invalid-feedback
                      v-if="getErrorState('oxygenOil')"
                      id="input-live-feedback-oxygen-oil"
                    >
                      <span v-html="getValidationFeedback('oxygenOil')"></span>
                    </b-form-invalid-feedback>
                  </s-input-label-edit>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
      </div>

      <div class="isolation-aging-parameters-general mb-3 avoid-break">
        <div class="isolation-aging-parameters-general__title">
          {{ $t('engineering.isolation-aging.parameters-general-section') }}
        </div>
        <div class="p-3">
          <b-row>
            <b-col sm="6">
              <div
                v-if="isVisible('totalAssetLifeExpectancyInYears')"
                class="isolation-aging-parameters-general__input form-group"
              >
                <label>
                  {{ getLabel('totalAssetLifeExpectancyInYears') }}<span v-if="isRequired(
                    'totalAssetLifeExpectancyInYears')
                  ">*</span>
                </label>
                <s-input-label-edit
                  v-model="$v.parameters.totalAssetLifeExpectancyInYears.$model"
                  :formatted-value="getFormattedNumber(
                    $v.parameters.totalAssetLifeExpectancyInYears.$model,
                    'totalAssetLifeExpectancyInYears'
                  )"
                  :editable="editMode"
                >
                  <s-number-input
                    class="text-right"
                    v-model="$v.parameters.totalAssetLifeExpectancyInYears.$model"
                    :numeric-options="getNumericOptions('totalAssetLifeExpectancyInYears')"
                    :validation="$v.parameters['totalAssetLifeExpectancyInYears']"
                    :invalid="getErrorState('totalAssetLifeExpectancyInYears')"
                    :disabled="isDisabled('totalAssetLifeExpectancyInYears')"
                  />
                  <b-form-invalid-feedback v-if="getErrorState('totalAssetLifeExpectancyInYears')">
                    <span v-html="getValidationFeedback('totalAssetLifeExpectancyInYears')"></span>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ReferenceHumidity,
  HumidityCurve,
} from 'sigma-mappings';
import {
  formatter as dateTimeFormatter,
  joinISODateAndTimeString,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import validateDateMixin from '@/components/mixins/validatePeriodDateMixin';
import InfoBadge from '@/components/icon/InfoBadge';
import parametersFields
  from '@/domain/data/installation/engineering/isolationAging/parametersFields';
import validations from '@/domain/data/installation/engineering/isolationAging/validations';
import enumFilter from '@/filters/enumFilter';

const FIXED_PERIOD_INTERVAL = 1;
const FIXED_PERIOD_INTERVAL_TYPE = 'days';

export default {
  name: 'IsolationAgingParametersForm',
  props: {
    parameters: {
      type: Object,
      required: true,
    },
    humiditySource: {
      type: Array,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    installationTimeZone: {
      type: String,
      default: '',
    },
  },
  components: {
    InfoBadge,
  },
  mixins: [
    validateDateMixin,
  ],
  data: () => ({
    referenceHumidity: ReferenceHumidity,
    humidityCurve: HumidityCurve,
  }),
  validations() {
    const parameters = {
      ...this.parameters,
      ...this.dateTimeValidations,
    };
    return validations(parameters);
  },
  computed: {
    titleTooltip() {
      return this.$t('messages.MI029.message', { timezone: this.installationTimeZone });
    },
    initialDate() {
      return this.parameters.initialDateTime
        ? setToStartOfDay(this.parameters.initialDateTime)
        : this.parameters.initialDate;
    },
    finalDate() {
      return this.parameters.finalDateTime
        ? setToStartOfDay(this.parameters.finalDateTime)
        : this.parameters.finalDate;
    },
    initialTime() {
      return this.parameters.initialDateTime
        ? dateTimeFormatter(this.parameters.initialDateTime, formats.ISO_TIME)
        : this.parameters.initialTime;
    },
    finalTime() {
      return this.parameters.finalDateTime
        ? dateTimeFormatter(this.parameters.finalDateTime, formats.ISO_TIME)
        : this.parameters.finalTime;
    },
    hasUserPermission() {
      return this.$hasUserPermission('PER013');
    },
    isInvalid() {
      return this.$v.$invalid;
    },
    humiditySourceOptions() {
      return (this.humiditySource || []).map(item => ({
        text: this.$t(`enums.engineering.isolation-aging.${item.id}`),
        value: item.id,
      }));
    },
    parametersFields() {
      const parameters = {
        ...this.parameters,
        ...this.dateTimeValidations,
      };
      return parametersFields(parameters);
    },
    dateTimeValidations() {
      return {
        isDateInvalid: this.isDateInvalid,
        isInitialDateInvalid: this.isInitialDateInvalid,
        isFinalDateInvalid: this.isFinalDateInvalid,
        isInitialMinDateInvalid: this.isInitialMinDateInvalid,
        isFinalMinDateInvalid: this.isFinalMinDateInvalid,
        isSameDate: this.isSameDate,
        isDateRelationInvalid: this.isDateRelationInvalid,
        isDateIntervalInvalid: this.isDateIntervalInvalid,
      };
    },
  },
  filters: {
    enumFilter,
  },
  methods: {
    getFormattedNumber(value, fieldName) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: this.parametersFields[fieldName].precision,
        maximumFractionDigits: this.parametersFields[fieldName].precision,
      });
    },
    getFormattedDate(date) {
      if (!date) return null;
      return dateTimeFormatter(date, formats.DATE_SHORT);
    },
    getLabel(fieldName) {
      return this.$t(this.parametersFields[fieldName].label);
    },
    getNumericOptions(fieldName) {
      const precision = this.parametersFields[fieldName].precision || 0;
      return {
        decimalPlaces: precision,
      };
    },
    getErrorState(fieldName) {
      return this.$v.parameters[fieldName].$error;
    },
    getErrorBsState(fieldName) {
      return this.$bsStateValidator(this.$v.parameters[fieldName].$invalid);
    },
    getValidationFeedback(fieldName) {
      return this.$t('messages.MA086.message',
        {
          minValue: this.getFormattedNumber(this.parametersFields[fieldName].minValue, fieldName),
          maxValue: this.getFormattedNumber(this.parametersFields[fieldName].maxValue, fieldName),
        });
    },
    showDateValidationFeedback() {
      this.$pushAlert({ code: 'MA006' });
    },
    showDateIntervalValidationFeedback() {
      this.$pushAlert({ code: 'MA096' });
    },
    getMinDateValidationFeedback(fieldName) {
      return this.$t('messages.MA038.message', {
        NomeDoCampo: this.getLabel(fieldName),
        FaixaInicial: dateTimeFormatter(this.minDate),
      });
    },
    isDisabled(fieldName) {
      return this.parametersFields[fieldName].disabled || !this.editMode || this.isSaving;
    },
    isRequired(fieldName) {
      return this.parametersFields[fieldName].required && this.editMode;
    },
    isVisible(fieldName) {
      return !this.parametersFields[fieldName].hidden;
    },
    getEnumFirstValueByI18n(enumList) {
      const path = 'enums.engineering.isolation-aging';
      const i18nList = [];
      Object.values(enumList)
        .forEach(item => i18nList.push({
          i18n: this.$t(`${path}.${item}`),
          value: item,
        }));
      const [first] = i18nList.sort((a, b) => (a.i18n > b.i18n ? 1 : -1));
      return first.value;
    },
    onChangeFixedPeriodHandler(state) {
      this.parameters.initialDate = null;
      this.parameters.finalDate = null;
      this.parameters.initialTime = null;
      this.parameters.finalTime = null;

      if (state) {
        this.parameters.initialDate = this.parametersFields.initialDate.default;
        this.parameters.finalDate = this.parametersFields.finalDate.default;
        this.parameters.initialTime = this.parametersFields.initialTime.default;
        this.parameters.finalTime = this.parametersFields.finalTime.default;
      }

      this.validateDates();
    },
    onChangeDateTime(field) {
      let valueToValidate;

      if (field.includes('initial')) {
        this.parameters.initialDateTime = joinISODateAndTimeString(
          this.parameters.initialDate,
          this.parameters.initialTime,
        );
        valueToValidate = this.parameters.initialDateTime;
      }

      if (field.includes('final')) {
        this.parameters.finalDateTime = joinISODateAndTimeString(
          this.parameters.finalDate,
          this.parameters.finalTime,
        );
        valueToValidate = this.parameters.finalDateTime;
      }

      this.validateDate(valueToValidate, field, null, this.showDateValidationFeedback);
      if (!this.isDateRelationInvalid) {
        this.validateDateInterval(
          FIXED_PERIOD_INTERVAL,
          FIXED_PERIOD_INTERVAL_TYPE,
          this.showDateIntervalValidationFeedback,
        );
      }
    },
    onChangeConsiderHumidityHandler(state) {
      if (state) {
        [this.parameters.paperHumiditySource] = Object
          .values((this.humiditySourceOptions || []).map(item => item.value));
        this.parameters.referenceHumidity = this.getEnumFirstValueByI18n(ReferenceHumidity);
        this.parameters.humidityCurveMethod = this.getEnumFirstValueByI18n(HumidityCurve);
        return;
      }

      this.parameters.paperHumiditySource = null;
      this.parameters.paperHumidity = null;
      this.parameters.referenceHumidity = null;
      this.parameters.referenceHumidityPercentage = null;
      this.parameters.humidityCurveMethod = null;
      this.parameters.oxygenOil = null;
    },
    onChangeReferenceHumidityHandler() {
      this.parameters.referenceHumidityPercentage = this.parametersFields
        .referenceHumidityPercentage.default;
    },
    onChangeHumidityCurveMethodHandler() {
      this.parameters.oxygenOil = this.parametersFields.oxygenOil.default;
    },
    validateDates() {
      this.onChangeDateTime('initial-date');
      this.onChangeDateTime('final-date');
    },
  },
  watch: {
    isInvalid: {
      handler(newValue) {
        this.$emit('invalid-form', newValue);
      },
      immediate: true,
    },
    editMode: {
      handler() {
        this.$v.$reset();
        this.validateDates();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.isolation-aging-parameters
  &-general
    padding: 5px
    border: 1px solid $color-border-gray
    border-radius: 3px
    &__title
      padding: .35rem 1rem
      border-radius: 5px
      font-weight: bold
      background-color: var(--color-background-dark)
    &__input
      & .label-edit_editing > input
        text-align: right
      & .label-edit_viewing
        opacity: .65
    &__select
      & .label-edit_viewing
        opacity: .65

+media-min-md()
  .isolation-aging-parameters
    &-general
      &__input
        & .label-edit_editing > input
          max-width: 9em
+media-max-sm()
  .isolation-aging-parameters
    &-general
      &__input
        & .label-edit_editing > input
          max-width: 100%
</style>
